import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { Dice5, Timer, Trophy, Users, Play, ArrowRight, BarChart3 } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1547638375-ebf04735d792?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwyfHxib2FyZCUyMGdhbWUlMjBwaWVjZXN8ZW58MHx8fHwxNzg1MjU1NDc2fDA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950">
      <Nav />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/40 via-slate-950/80 to-slate-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-36 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/5 text-amber-300 text-xs font-mono-data tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" /> LIVE TOURNAMENT ENGINE
            </div>
            <h1 className="mt-6 text-5xl md:text-7xl lg:text-8xl font-bold text-slate-50 leading-[0.95]">
              Run tabletop<br />
              tournaments <span className="text-amber-400">like a pro.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-slate-400 max-w-2xl">
              Auto-seat tables, track normalized victory-point scores across rounds, and beam a
              live scoreboard to everyone who couldn't make it in person.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/auth?mode=register" data-testid="hero-cta-start">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold h-14 px-8 text-base">
                  Start Your Tournament <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/auth" data-testid="hero-cta-signin">
                <Button size="lg" variant="outline" className="border-white/10 bg-white/5 text-slate-100 hover:bg-white/10 h-14 px-8">
                  I have an account
                </Button>
              </Link>
            </div>
            <div className="mt-16 grid grid-cols-3 gap-6 max-w-xl">
              <Stat n="27%" l="of table pool" />
              <Stat n="∞" l="rounds tracked" />
              <Stat n="1st" l="podium reveal" />
            </div>
          </div>
          <div className="lg:col-span-4 relative">
            <div className="glass rounded-2xl p-6 border border-white/10">
              <div className="text-xs text-slate-500 font-mono-data tracking-widest">CURRENT ROUND</div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="text-6xl font-bold text-amber-400 font-mono-data">03</span>
                <span className="text-slate-500">/ 05</span>
              </div>
              <div className="mt-6 font-mono-data text-7xl text-slate-50 leading-none">17:42</div>
              <div className="mt-6 space-y-2">
                {[
                  { name: "Table 1", pts: 41 },
                  { name: "Table 2", pts: 33 },
                  { name: "Table 3", pts: 26 },
                  { name: "BYE", pts: null },
                ].map((r, i) => (
                  <div key={i} className={`flex items-center justify-between px-3 py-2.5 rounded-md border ${r.name === 'BYE' ? 'border-rose-500/30 bg-rose-500/5' : 'border-white/5 bg-slate-900/50'}`}>
                    <span className={`text-sm ${r.name === 'BYE' ? 'text-rose-300' : 'text-slate-200'}`}>{r.name}</span>
                    <span className="font-mono-data text-sm text-slate-400">{r.pts !== null ? `${r.pts} VP` : "SIT OUT"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={<Users className="w-6 h-6" />} title="Auto-seating & BYEs" body="Randomize seats each round. Uneven counts? BYE rotation handles it automatically." />
          <Feature icon={<Timer className="w-6 h-6" />} title="Round timer with alarm" body="A tabletop-sized timer with an audio alarm the whole room can hear." />
          <Feature icon={<BarChart3 className="w-6 h-6" />} title="Three scoring types" body="Table-normalized average · Fixed placement points (5/3/1) · Single-elimination bracket. Pick per tournament." />
          <Feature icon={<Play className="w-6 h-6" />} title="Live observer view" body="Share a public URL. Friends and family follow along in real time." />
          <Feature icon={<Trophy className="w-6 h-6" />} title="Championship & Podium" body="Top-ranked players advance to a final round. Confetti podium reveal included." />
          <Feature icon={<Dice5 className="w-6 h-6" />} title="Archive & PDF Export" body="Every finished tournament archives cleanly with a printable PDF summary." />
        </div>
      </section>

      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between text-sm text-slate-500">
          <span>© Meeple.Match — Tournament OS</span>
          <span className="font-mono-data text-xs tracking-widest">BUILT FOR TABLETOP</span>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="text-3xl md:text-4xl font-bold text-slate-100 font-mono-data">{n}</div>
    <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{l}</div>
  </div>
);

const Feature = ({ icon, title, body }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900/40 p-8 hover:border-amber-500/30 transition-colors duration-300">
    <div className="w-12 h-12 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-400">{icon}</div>
    <h3 className="mt-6 text-xl text-slate-100">{title}</h3>
    <p className="mt-2 text-slate-400 text-sm leading-relaxed">{body}</p>
  </div>
);
